/***********************/
/* Angular + external */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { ObjectUtils } from "@bfx/primeng/utils";
import { Subscription } from "rxjs";
/***********************/
/* BFX */
// services
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OEApiService } from "../services/oe-api.service";
import { OECoordinatorService } from "../services/oe-coordinator.service";
// enums
import { OENActionButtonProperty } from "../data/enums/oe-action-button-property.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
// mappings
import {
  ACTION_BTN_TO_DISPLAY_MAPPING,
  ACTION_ENTRY_DISPLAY_STATES,
} from "../data/mappings/oe-actions-btn-display.mapping";
// models
import { runDetached } from "../../../shared/helper-services/run-detached";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { OEPanelActionsModel } from "../data/models/oe-panel-actions.model";
import { OELoggerService } from "../services/basic/oe-logger.service";

@Component({
  selector: "bfx-panel-actions",
  templateUrl: "./html/bfx-panel-actions.html",
  standalone: false,
})
export class OEActionsPanelComponent implements OnDestroy, OnInit, AfterViewInit, OnChanges {
  @Input() rootRefId: string;
  @Input() refId: string;
  @Input() entryType: OENEntryType;
  @Input() scratchpad: boolean;
  @Input() loadingComplete: boolean;
  @Input() operationMode: OENOperationMode;

  /***************************************/
  /* DATA */

  // Hoist enum for template
  public OENDisplayState = OENDisplayState;
  public OENEntryType = OENEntryType;

  // Model
  public data = new OEPanelActionsModel();
  private control: {
    loadingTimeout: any;
    loadingTimeoutLimit: number;
  } = {
    loadingTimeout: null,
    loadingTimeoutLimit: 5 * 1000, // 5 sec
  };
  // Display state
  public _displayState: OENDisplayState = OENDisplayState.SHOW_LOADING;
  public get displayState(): OENDisplayState {
    return this._displayState;
  }
  public set displayState(newDisplayState: OENDisplayState) {
    if (OENDisplayState.getAllElements().includes(newDisplayState)) {
      if (ACTION_ENTRY_DISPLAY_STATES.includes(this._displayState)) {
        // loading completion fallback
        this.control.loadingTimeout = setTimeout(() => {
          if (!this.data.dataLoadingComplete) {
            this.data.dataLoadingComplete = true;
          }
        }, this.control.loadingTimeoutLimit);
      }
      this._displayState = newDisplayState;
    } else {
      logger.out.error(
        `ERROR _oePanelActions.setDisplayState(): Unknown display state '${newDisplayState}'`,
      );
    }
  }

  private coordinatorSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    protected oeLogger: OELoggerService,
    private coordinator: OECoordinatorService,
    private api: OEApiService,
    public cd: ChangeDetectorRef,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.initialize();
  }

  ngOnInit() {
    this.updateSaveClearButtonVisibility();
  }

  private updateSaveClearButtonVisibility() {
    let isThisAModify =
      this.api.isOperationModeModify(this.refId) || this.api.isOperationModeModifyInfo(this.refId);
    let isBulking = this.api.isBulking(this.refId);

    this.data.button_visibility.save =
      this.entryType !== OENEntryType.ALGO &&
      this.entryType !== OENEntryType.NDF_ALGO &&
      this.entryType !== OENEntryType.GAMMA &&
      isBulking === false &&
      (this.scratchpad || !isThisAModify || this.api.isOperationModeEdit(this.refId));

    this.data.button_visibility.clear = !isThisAModify || this.scratchpad;
    // No point in aborting a scratchpad edit as unlock events will never get to the Entry UI
    this.data.button_visibility.modifyAbort =
      this.api.isOperationModeModify(this.refId) && !this.scratchpad;
  }

  ngOnDestroy() {
    if (this.coordinatorSubscription != null) {
      this.coordinatorSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    this.initializePermissions();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      if (propName === "loadingComplete") {
        if (typeof this.loadingComplete === "boolean") {
          this.oeLogger.outputDevLogToConsole(
            `DEV: ActionsPanel -> DATA-LOADING = ${
              this.loadingComplete ? "COMPLETE" : "IN-PROGRESS"
            }`,
          );
          // just a check
          if (this.data.dataLoadingComplete && !this.loadingComplete) {
            this.oeLogger.outputDevLogToConsole(
              `DEV: ActionsPanel -> DATA-LOADING = Was complete, and now its not?|`,
              true,
            );
            this.displayState = this._displayState; // activate the loading completion fallback
          }
          // cancel loading completion fallback
          if (this.loadingComplete && this.control.loadingTimeout != null) {
            clearTimeout(this.control.loadingTimeout);
          }
          this.data.dataLoadingComplete = this.loadingComplete;
        }
      }
      if (propName == "operationMode") {
        logger.out.info(`opa-c - setting operation mode ${OENOperationMode[this.operationMode]} `);
      }
    }
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.subscribeToCoordinator();
  }

  private initializePermissions(): void {
    if (typeof this.rootRefId === "string" && this.rootRefId.length > 1) {
      this.data.permissions.submit = this.api.getPermissionEnterOrder(this.rootRefId);
    } else {
      logger.out.error(
        `ERROR: _oePanelActions.initializePermissions(): Unknown refId: '${this.rootRefId}'`,
      );
    }
  }

  private subscribeToCoordinator(): void {
    this.coordinatorSubscription = this.coordinator.actionsPanelCmd$
      .pipe(runDetached(this))
      .subscribe(($event: OECommandEventModel) => {
        if ($event.ref === this.rootRefId || $event.ref === this.refId) {
          if (!ObjectUtils.equals($event, this.data.last_processed_event)) {
            // ignore if duplicate
            this.data.last_processed_event = $event;

            this.oeLogger.outputDevLogToConsole(
              `DEV: ActionsPanel -> UPDATE: ${OENCommand[$event.cmd]}` +
                `${
                  $event.data != null && $event.data.acceptance != null
                    ? " |  ACCEPT: " + $event.data.acceptance
                    : ""
                }` +
                `${
                  $event.data != null && $event.data.approval != null
                    ? " |  APPROVE: " + $event.data.approval
                    : ""
                }` +
                `${
                  $event.data != null && $event.data.rejection != null
                    ? " |  REJECT: " + $event.data.rejection
                    : ""
                }` +
                ` | Event: `,
              false,
              $event,
            );

            switch ($event.cmd) {
              case OENCommand.LOCK_ALL:
                this.lockAll();
                break;
              case OENCommand.UNLOCK_ALL:
                this.unlockAll();
                break;
              case OENCommand.BULKING:
              case OENCommand.BULKED:
              case OENCommand.UNBULK:
                //
                this.updateSaveClearButtonVisibility();
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "modifyAbort",
                  "save",
                ]);
                this.data.button_locks.save = !(
                  $event.cmd !== OENCommand.BULKING &&
                  this.operationMode !== OENOperationMode.MODIFY
                );
                this.data.button_locks.clear = !(this.operationMode !== OENOperationMode.MODIFY);

                break;
              case OENCommand.STATE_ORDER_ENTRY:
                if (this.scratchpad) {
                  this.data.button_visibility.clear = this.data.button_visibility.save = true;
                  this.data.button_locks.clear = this.data.button_locks.save = false;
                  this.data.button_visibility.modifyAbort = false;

                  this.displayState = OENDisplayState.SHOW_ORDER_ENTRY;
                  this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                    "modifyAbort",
                    "clear",
                    "save",
                  ]);
                  this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                    "modifyAbort",
                    "clear",
                    "save",
                  ]);
                  this.updateSaveClearButtonVisibility();
                  break;
                }
              case OENCommand.STATE_ORDER_IN_MODIFICATION:
                let isThisAModify =
                  this.api.isOperationModeModify(this.refId) ||
                  this.api.isOperationModeModifyInfo(this.refId);
                this.data.button_visibility.clear = !isThisAModify || this.scratchpad;
                this.data.button_locks.clear = isThisAModify && !this.scratchpad;

                this.data.button_visibility.modifyAbort = this.api.isOperationModeModify(
                  this.refId,
                );
                this.data.button_locks.modifyAbort = !isThisAModify;

                this.updateSaveClearButtonVisibility();
                this.displayState = OENDisplayState.SHOW_ORDER_ENTRY;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "clear",
                  "save",
                  "modifyAbort",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "clear",
                  "save",
                  "modifyAbort",
                ]);

                break;
              case OENCommand.STATE_ORDER_PROCESSING:
                this.data.button_locks.submit = true;
                this.displayState = OENDisplayState.SHOW_PROCESSING;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["submit"]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.STATE_ORDER_ENTRY_NEGATIVE_MARKUP:
                this.data.button_locks.edit = false;
                this.data.button_locks.negativeMarkupAccept = false;
                this.displayState = OENDisplayState.SHOW_NEGATIVE_MARKUP_WARN;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "edit",
                  "negativeMarkupAccept",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.BM_ZERO_AMOUNT_WARN:
                this.data.button_locks.edit = false;
                this.data.button_locks.negativeMarkupAccept = false;
                this.displayState = OENDisplayState.SHOW_BM_ZERO_AMOUNT_WARN;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["edit", "accept"]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR:
              case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN:
                this.data.button_locks.toleranceAccept = false;
                this.displayState = OENDisplayState.SHOW_TOLERANCE;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["toleranceAccept"]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.STATE_ORDER_MODIFICATION_EXTERNAL:
                // Order is being modified by someone else so lets not allow thm to copy as contents
                // may not be stable
                this.displayState = OENDisplayState.SHOW_MONITOR_EXECUTION;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "close",
                  "duplicate",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "close",
                  "duplicate",
                ]);

                this.data.button_visibility.new = false;
                this.data.button_visibility.copy = false;
                this.data.button_visibility.close = true;
                this.data.button_locks.close = false;
                break;
              case OENCommand.STATE_MONITOR_EXECUTION:
                this.displayState = OENDisplayState.SHOW_MONITOR_EXECUTION;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, []);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, []);
                break;
              case OENCommand.STATE_ORDER_SUCCESS:
                this.displayState = OENDisplayState.SHOW_SUCCESS;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["close", "new"]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, ["close", "new"]);

                this.data.button_locks.new = !(
                  this.api.isOperationModeNew(this.refId) ||
                  this.api.isOperationModeDuplicate(this.refId)
                );
                this.data.button_visibility.new =
                  this.api.isOperationModeNew(this.refId) ||
                  this.api.isOperationModeDuplicate(this.refId);

                this.data.button_locks.close =
                  $event.data != null && $event.data.close != null && !$event.data.close;
                this.data.button_visibility.close = !(
                  $event.data != null &&
                  $event.data.close != null &&
                  !$event.data.close
                );

                this.data.button_locks.duplicate = !(
                  this.api.isOperationModeModify(this.refId) ||
                  this.api.isOperationModeModifyInfo(this.refId)
                );
                this.data.button_visibility.duplicate =
                  this.api.isOperationModeModify(this.refId) ||
                  this.api.isOperationModeModifyInfo(this.refId);

                this.data.button_locks.copy =
                  this.api.isOperationModeModify(this.refId) ||
                  this.api.isOperationModeModifyInfo(this.refId);
                this.data.button_visibility.copy = !(
                  this.api.isOperationModeModify(this.refId) ||
                  this.api.isOperationModeModifyInfo(this.refId)
                );

                break;
              case OENCommand.STATE_ORDER_REJECTED:
              case OENCommand.STATE_ORDER_REJECTED_HCO_ERROR:
              case OENCommand.STATE_ORDER_INT_ERROR:
                // AMEND_MARKUP, MODIFY_INFO
                if (
                  this.api.isOperationModeModifyInfo(this.refId) ||
                  this.api.isOperationModeAmendMarkup(this.refId)
                ) {
                  this.data.button_visibility.edit = false;
                  this.data.button_locks.edit = true;
                  this.data.button_visibility.duplicate = false;
                  this.data.button_locks.duplicate = true;

                  // temporary commented out edit restrictions on modify - TBD is its permanent
                  /* }else if(this.api.isOperationModeModify(this.refId)){ // MODIFY
									
									let externalTG = this.api.determineExternalTraderGroupFromBaseLookup(this.refId); /// !this.api.determineExternalTraderGroupOnEntry(this.refId))
									if(externalTG === null){
										externalTG = true;//When extTG can not be determined, err on the side of caution							
									}
									this.data.button_visibility.edit = !externalTG;
									this.data.button_locks.edit = externalTG;
									this.data.button_visibility.duplicate = true;
									this.data.button_locks.duplicate = false; */
                } else {
                  // NEW, DUPLICATE
                  if (OENCommand.STATE_ORDER_REJECTED_HCO_ERROR === $event.cmd) {
                    this.data.button_visibility.edit = false;
                    this.data.button_locks.edit = true;
                  } else {
                    const showEdit: boolean =
                      !this.api.monitoringExecution(this.refId) &&
                      !this.api.isOperationModeMonitorExecution(this.refId);
                    this.data.button_visibility.edit = showEdit;
                    this.data.button_locks.edit = !showEdit;
                    const showAbort =
                      showEdit &&
                      this.api.supportsMonitoringExecution(this.refId) &&
                      !this.api.isOperationModeNew(this.refId) &&
                      !this.api.isOperationModeDuplicate(this.refId);
                    this.data.button_visibility.modAbort = showAbort;
                    this.data.button_locks.modAbort = !showAbort;
                  }
                  this.data.button_visibility.duplicate = true;
                  this.data.button_locks.duplicate = false;
                }
                this.displayState = OENDisplayState.SHOW_REJECTED;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "modAbort",
                  "edit",
                  "duplicate",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "modAbort",
                  "edit",
                  "duplicate",
                ]);
                break;
              case OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE:
                this.data.button_locks.modAccept =
                  $event.data != null && $event.data.acceptance != null && !$event.data.acceptance;
                this.data.button_locks.modReject =
                  $event.data != null && $event.data.rejection != null && !$event.data.rejection;
                this.data.button_locks.modAbort =
                  $event.data != null && $event.data.abort != null && !$event.data.abort;
                this.data.button_visibility.modAccept = !(
                  $event.data != null &&
                  $event.data.acceptance != null &&
                  !$event.data.acceptance
                );
                this.data.button_visibility.modReject = !(
                  $event.data != null &&
                  $event.data.rejection != null &&
                  !$event.data.rejection
                );
                this.data.button_visibility.modAbort = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.abort
                );

                this.displayState = OENDisplayState.SHOW_MODIFY_OUT_ACCEPTANCE;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "modAccept",
                  "modReject",
                  "modAbort",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "modAccept",
                  "modReject",
                  "modAbort",
                ]);
                break;
              case OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE:
                this.data.button_locks.modAccept =
                  $event.data != null && $event.data.acceptance != null && !$event.data.acceptance;
                this.data.button_locks.modReject =
                  $event.data != null && $event.data.rejection != null && !$event.data.rejection;
                this.data.button_locks.modAbort =
                  $event.data != null && $event.data.abort != null && !$event.data.abort;
                this.data.button_visibility.modAccept = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.acceptance
                );
                this.data.button_visibility.modReject = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.rejection
                );
                this.data.button_visibility.modAbort = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.abort
                );

                this.displayState = OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "modAccept",
                  "modReject",
                  "modAbort",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "modAccept",
                  "modReject",
                  "modAbort",
                ]);
                break;
              case OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED:
                this.data.button_locks.trAccept =
                  $event.data != null && $event.data.acceptance != null && !$event.data.acceptance;
                this.data.button_locks.trReject =
                  $event.data != null && $event.data.rejection != null && !$event.data.rejection;
                this.data.button_locks.trAbort =
                  $event.data != null && $event.data.abort != null && !$event.data.abort;
                this.data.button_visibility.trAccept = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.acceptance
                );
                this.data.button_visibility.trReject = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.rejection
                );
                this.data.button_visibility.trAbort = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.abort
                );

                this.displayState = OENDisplayState.SHOW_ACCEPTANCE_ALLOWED;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "trAccept",
                  "trReject",
                  "trAbort",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "trAccept",
                  "trReject",
                  "trAbort",
                ]);
                break;
              case OENCommand.STATE_ORDER_ACCEPTANCE_CALL:
                this.data.button_locks.trAccept =
                  $event.data != null && $event.data.acceptance != null && !$event.data.acceptance;
                this.data.button_locks.trReject =
                  $event.data != null && $event.data.rejection != null && !$event.data.rejection;
                this.data.button_locks.trAbort =
                  $event.data != null && $event.data.abort != null && !$event.data.abort;
                this.data.button_visibility.trAccept = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.acceptance
                );
                this.data.button_visibility.trReject = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.rejection
                );
                this.data.button_visibility.trAbort = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.abort
                );

                this.displayState = OENDisplayState.SHOW_ACCEPTANCE_CALL;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "trAccept",
                  "trReject",
                  "trAbort",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "trAccept",
                  "trReject",
                  "trAbort",
                ]);
                break;
              case OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL:
                this.data.button_locks.trAccept =
                  $event.data != null && $event.data.acceptance != null && !$event.data.acceptance;
                this.data.button_locks.trReject =
                  $event.data != null && $event.data.rejection != null && !$event.data.rejection;
                this.data.button_locks.trAbort =
                  $event.data != null && $event.data.abort != null && !$event.data.abort;
                this.data.button_visibility.trAccept = !(
                  $event.data != null &&
                  $event.data.acceptance != null &&
                  !$event.data.acceptance
                );
                this.data.button_visibility.trReject = !(
                  $event.data != null &&
                  $event.data.rejection != null &&
                  !$event.data.rejection
                );
                this.data.button_visibility.trAbort = !(
                  $event.data != null &&
                  $event.data.abort != null &&
                  !$event.data.abort
                );

                this.displayState = OENDisplayState.SHOW_ACCEPTANCE_EXTERNAL;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "trAccept",
                  "trReject",
                  "trAbort",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "trAccept",
                  "trReject",
                  "trAbort",
                ]);
                break;
              case OENCommand.STATE_ORDER_APPROVAL_ALLOWED:
                this.data.button_locks.diApprove =
                  $event.data != null && $event.data.approval != null && !$event.data.approval;
                this.data.button_locks.diReject =
                  $event.data != null && $event.data.rejection != null && !$event.data.rejection;

                this.displayState = OENDisplayState.SHOW_APPROVAL_ALLOWED;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "diApprove",
                  "diReject",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.STATE_ORDER_APPROVAL_INTERNAL:
                this.displayState = OENDisplayState.SHOW_APPROVAL_INTERNAL;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, null);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.STATE_ORDER_APPROVAL_EXTERNAL:
                this.displayState = OENDisplayState.SHOW_APPROVAL_EXTERNAL;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, null);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.STATE_ORDER_UNLOCKED:
                // duplicate
                this.data.button_locks.duplicate =
                  $event.data != null &&
                  $event.data.hideDuplicate != null &&
                  $event.data.hideDuplicate;
                this.data.button_visibility.duplicate =
                  $event.data != null &&
                  $event.data.hideDuplicate != null &&
                  !$event.data.hideDuplicate;
                // edit
                this.data.button_locks.edit =
                  $event.data != null && $event.data.hideEdit != null && $event.data.hideEdit;
                this.data.button_visibility.edit =
                  $event.data != null && $event.data.hideEdit != null && !$event.data.hideEdit;

                this.displayState = OENDisplayState.SHOW_UNLOCKED;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["duplicate", "edit"]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
                  "duplicate",
                  "edit",
                ]);
                break;
              case OENCommand.STATE_ORDER_LOST_HIDDEN:
                // Order had gone out of visibility
                this.displayState = OENDisplayState.SHOW_LOST_HIDDEN;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, []);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, []);
                break;
              case OENCommand.STATE_ORDER_AMEND_MARKUP:
                this.data.button_locks.diApprove =
                  $event.data != null && $event.data.approval != null && !$event.data.approval;
                this.data.button_locks.diReject =
                  $event.data != null && $event.data.rejection != null && !$event.data.rejection;

                this.displayState = OENDisplayState.SHOW_AMEND_MARKUP;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
                  "diApprove",
                  "diReject",
                ]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, null);
                break;
              case OENCommand.STATE_ORDER_EXPIRY_PENDING:
                // new
                this.data.button_locks.new =
                  $event.data != null && $event.data.close != null && $event.data.close;
                this.data.button_visibility.new = !(
                  $event.data != null &&
                  $event.data.close != null &&
                  $event.data.close
                );
                // close
                this.data.button_locks.close =
                  $event.data != null && $event.data.close != null && !$event.data.close;
                this.data.button_visibility.close = !(
                  $event.data != null &&
                  $event.data.close != null &&
                  !$event.data.close
                );

                this.displayState = OENDisplayState.SHOW_EXPIRY_PENDING;
                this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["new", "close"]);
                this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, ["new", "close"]);
                break;
              case OENCommand.STATE_REVAL_BUTTONS:
                this.revalButtons();
                break;
              case OENCommand.UPDATE_SAVE_BTN_AVAILABILITY:
                if (typeof $event.data === "boolean") {
                  this.data.saveDisabled = $event.data;
                }
                break;
              case OENCommand.UPDATE_GAMMA_INELIGIBLE:
                this.data.button_locks.submit = true;
                break;
              case OENCommand.UPDATE_GAMMA_ELIGIBLE:
                this.data.button_locks.submit = false;
                break;
              case OENCommand.GAMMA_ENABLE_CUST_BUTTON:
                break;
              case OENCommand.GAMMA_DISABLE_CUST_BUTTON:
                break;
              case OENCommand.PROPAGATE_CLIENT_INTENT_CHANGE:
                this.data.submitClientIntent = $event.data;
                break;
              default:
                logger.out.error(
                  "ERROR: BfxFormActionsPanelComponent.subscribeToCoordinator(): Command not implemented! command= %s",
                  OENCommand[$event.cmd],
                );
                break;
            }
          } else {
            //logger.out.info(`Actions panel update IGNORED cmd: ${OENCommand[$event.cmd]} | on event: `, $event);
            if ($event.cmd === OENCommand.STATE_REVAL_BUTTONS) {
              // No idea why this is outside the refId check - but functionality is same as reval above
              this.revalButtons();
            }
          }
        }
      });
  }

  /**********************************************************************/
  /* PRIVATE METHODS */

  private updateButtonProperties(
    property: OENActionButtonProperty,
    doNotResetList: Array<string>,
  ): void {
    //logger.out.info(`--------------- ${BfxActionButtonProperty[property]} for ${OENDisplayState[this.data.actions_config]} `);
    let useList: boolean = Array.isArray(doNotResetList) && doNotResetList.length > 0;
    let data =
      property === OENActionButtonProperty.VISIBILITY
        ? this.data.button_visibility
        : this.data.button_locks;

    let displayedBtnList = ACTION_BTN_TO_DISPLAY_MAPPING.get(this.displayState);

    if (!Array.isArray(displayedBtnList)) {
      logger.out.info(
        `${this.constructor.name}updateButtonProperties() ignoring empty button list`,
      );
      return;
    }

    let itemsApplied = [];
    let itemsDefault = [];
    let itemsManaged = [];
    for (let key in data) {
      if (!useList || !doNotResetList.includes(key)) {
        // apply reset
        if (displayedBtnList.includes(key)) {
          data[key] = property === OENActionButtonProperty.VISIBILITY; // unlock and display
          itemsApplied.push(String(" " + key + ": " + data[key]));
        } else {
          data[key] = property !== OENActionButtonProperty.VISIBILITY; // lock and hide
          itemsDefault.push(String(" " + key + ": " + data[key]));
        }
      } else {
        itemsManaged.push(String(" " + key + ": " + data[key]));
      }
    }
    // logger.out.info(`MANAGED => `, itemsManaged.toString());
    // logger.out.info(`APPLIED => `, itemsApplied.toString());
    // logger.out.info(`DEFAULT => `, itemsDefault.toString());
  }

  private resetButtonProperties(property: OENActionButtonProperty): void {
    // logger.out.info(`---------------RESET - ${BfxActionButtonProperty[property]} for ${OENDisplayState[this.data.actions_config]} ---`);
    let data =
      property === OENActionButtonProperty.VISIBILITY
        ? this.data.button_visibility
        : this.data.button_locks;
    for (let key in data) {
      data[key] = property !== OENActionButtonProperty.VISIBILITY;
    }
  }

  public enableSave() {
    this.data.button_visibility.save =
      this.entryType !== OENEntryType.ALGO &&
      this.entryType !== OENEntryType.NDF_ALGO &&
      this.entryType !== OENEntryType.GAMMA &&
      this.api.isOperationModeModify(this.refId);
  }

  protected lockAll(): void {
    this.data.lockAllFg = true;
    this.updateLocking();
  }

  protected unlockAll(): void {
    this.data.lockAllFg = false;
    this.updateLocking();
  }

  private updateLocking(): void {
    if (!this.data.lockAllFg) {
      this.data.lock_controls = false;
    } else {
      this.data.lock_controls = true;
    }
  }

  /**********************************************************************/
  /* PUBLIC METHODS */

  public clear(): void {
    this.api.actionReset(this.rootRefId);
  }

  public save() {
    this.api.actionSaveOrder(this.rootRefId).then((result: boolean) => {
      if (!result) {
        let clearBtnInactive =
          (this.api.isOperationModeModify(this.refId) ||
            this.api.isOperationModeModifyInfo(this.refId)) &&
          !this.scratchpad;
        this.data.button_visibility.clear = !clearBtnInactive;
        this.data.button_locks.clear = clearBtnInactive;
        setTimeout(() => {
          this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["modifyAbort", "clear"]);
          this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, ["modifyAbort", "clear"]);
        }, 200);
      }
    });
  }

  public submit(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.coordinator.saveGammaModel(this.rootRefId);
    this.api.actionSubmitOrder(this.rootRefId).then((result: boolean) => {
      if (!result) {
        let clearBtnInactive =
          (this.api.isOperationModeModify(this.refId) && !this.scratchpad) ||
          this.api.isOperationModeModifyInfo(this.refId);
        this.data.button_visibility.clear = !clearBtnInactive;
        this.data.button_locks.clear = clearBtnInactive;
        this.data.button_visibility.modifyAbort = clearBtnInactive;
        this.data.button_locks.modifyAbort = !clearBtnInactive;
        setTimeout(() => {
          this.updateButtonProperties(OENActionButtonProperty.LOCKING, [
            "modifyAbort",
            "clear",
            "save",
          ]);
          this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, [
            "modifyAbort",
            "clear",
            "save",
          ]);
        }, 200);
      }
    });
  }

  // copy(edit)/duplicate
  public duplicate(): void {
    this.api.actionDuplicateOrder(this.rootRefId, false);
  }
  public copy(fromEdit: boolean = false, scratchpad: boolean = false): void {
    if (!fromEdit) {
      this.scratchpad = scratchpad = false;
    }

    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.api.actionEditOrder(this.refId, false, fromEdit, scratchpad);
  }
  public edit(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    if (this.operationMode !== OENOperationMode.MODIFY || this.scratchpad) {
      this.copy(true, this.scratchpad);
    } else {
      this.api.actionReInitModify(this.refId, this.operationMode);
    }
  }

  // abort
  public abort(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionAbort(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }
  // algo abort
  public modifyAbort(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionAbortToMonitoring(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }

  // dealer intervention
  public diReject(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionDiReject(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }

  public diApprove(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionDiApprove(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }
  // tolerance
  public toleranceEdit(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.copy(true, this.scratchpad);
  }
  public toleranceAccept(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.api.actionToleranceAccept(this.rootRefId);
  }
  // negative markup
  public negativeMarkupEdit(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.copy(true, this.scratchpad);
  }
  public negativeMarkupAccept(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.api.actionNegativeMarkupAccept(this.rootRefId);
  }
  public bmZeroAmountAccept(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.api.actionBmZeroAmountAccept(this.rootRefId);
  }
  public bmZeroAmountEdit(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.copy(true, this.scratchpad);
  }
  // modify acceptance needed
  public modifyAccept(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionModifyOutAccept(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }

  public modifyReject(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionModifyOutReject(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }

  // acceptance needed
  public trAccept(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionTrAccept(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }

  public trReject(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionTrReject(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }
  // acceptance needed CALL
  public trAcceptTop(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionTrAcceptTop(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }
  public trAcceptDown(): void {
    const initState = this._displayState;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    const serverResult = this.api.actionTrAcceptDown(this.rootRefId);
    this.revalButtonsOnFailedPromise(serverResult, initState);
  }
  // new
  public newOrder(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.api.actionNewOrder(this.rootRefId, this.entryType);
  }

  // close
  public close(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.api.actionIssueCloseOrderEntryCmd(this.rootRefId);
  }
  // Amend Markup
  public amCancel(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.close();
  }
  public amApprove(): void {
    let cancelBtnLock = this.data.button_locks.amCancel;
    let approveBtnLock = this.data.button_locks.amApprove;
    let rejectBtnLock = this.data.button_locks.amReject;
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    if (this.api.actionAmApprove(this.rootRefId)) {
      //
      setTimeout(() => {
        this.data.button_locks.amCancel = cancelBtnLock;
        this.data.button_locks.amApprove = approveBtnLock;
        this.data.button_locks.amReject = rejectBtnLock;
      }, 250);
    }
  }
  public amReject(): void {
    this.resetButtonProperties(OENActionButtonProperty.LOCKING);
    this.api.actionAmReject(this.rootRefId);
  }

  /**
   * Iterate over all buttons defined for the current display state and reinitialise
   */
  private revalButtons() {
    if (
      this.displayState === OENDisplayState.SHOW_REJECTED &&
      (this.api.isOperationModeModifyInfo(this.refId) ||
        this.api.isOperationModeAmendMarkup(this.refId) ||
        this.api.isOperationModeModify(this.refId))
    ) {
      this.updateButtonProperties(OENActionButtonProperty.LOCKING, ["edit", "duplicate"]);
      this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, ["edit", "duplicate"]);
    } else {
      let doNotResetList = this.api.isOperationModeModify(this.refId) ? ["clear", "save"] : [];

      this.updateButtonProperties(OENActionButtonProperty.LOCKING, doNotResetList);
      this.updateButtonProperties(OENActionButtonProperty.VISIBILITY, doNotResetList);
    }
  }

  /**
   * If Promise arg resolves to false, then reval all the buttons if the display state has not changed.
   *
   * Used to check all async server operations - if an operation fails, we want to make sure the buttons don't
   * stay disabled
   *
   * @param operationPromise
   * @param initState
   */
  private revalButtonsOnFailedPromise(
    operationPromise: Promise<CubiqTS.ErrorsImpl | CubiqTS.ErrorsImpl[]>,
    initState: OENDisplayState,
  ): void {
    operationPromise.then(
      (result) => {
        if (!Array.isArray(result)) {
          result = [result];
        }
        if (result?.length > 0) {
          if (this._displayState === initState) {
            logger.out.info(`oepa-c - Server operation failed - refreshing buttons`);
            this.revalButtons();
            this.processErrors(result);
          }
        }
      },
      (_result) => {
        if (this._displayState === initState) {
          logger.out.info(
            `oepa-c - Server operation failed with promise failure - refreshing buttons`,
          );
          this.revalButtons();
        } else {
          logger.out.info(
            `oepa-c - Server operation failed with promise failure - not refreshing buttons`,
          );
        }
      },
    );
  }

  /**
   * Perform any additional handling of errors - for the moment, there's is nothing we need
   * to do as a notification of fail is generated in the base service
   * @param result
   */
  private processErrors(_result: CubiqTS.ErrorsImpl[]) {
    // let errorMessages = [];
    // result.forEach((error) => {
    //   errorMessages = errorMessages.concat(this.errorHandler.getErrorMessages(error));
    // });
    // const order = this.getter.get(this.refId);
    // const orderId = order?.orderId ?? "<UNKNOWN>";
    // const orderRef = order?.orderRef ?? "<UNKNOWN>";
    // this.notificationsService.notifyServerOperationErrors(
    //   errorMessages,
    //   BFXNotificationType.ORDER_ACTION_FAIL,
    //   BFXNotificationSubType.REJECTIONS_FAIL,
    //   orderId,
    //   orderRef,
    // );
  }
}
